import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle, CalendarClock, MapPin, Target, Activity as ActivityIcon,
  Plus, Building2, ChevronRight, CheckCircle2, Sparkles,
} from "lucide-react";
import { todayApi, tasksApi, followupsApi } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { VisitOutcomeDialog } from "@/components/forms/VisitOutcomeDialog";
import { ChannelBadge } from "@/components/ChannelBadge";
import { PlanTodayPanel } from "@/components/PlanTodayPanel";
import { TodayChatCard } from "@/components/TodayChatCard";
import { NotificationPermissionPrompt } from "@/components/NotificationPermissionPrompt";
import { brand } from "@/theme/themeTokens";
import { toast } from "sonner";

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" }) : "");
const availTypeLabel = { field: "Field", phone: "Phone", email_desk: "Email/Desk" };

function Section({ title, icon: Icon, count, tone = "slate", children, testid }) {
  const tones = {
    rose: "text-rose-600 bg-rose-50",
    amber: "text-amber-700 bg-amber-50",
    teal: "text-teal-700 bg-teal-50",
    slate: "text-slate-500 bg-slate-100",
  };
  return (
    <section className="mt-5" data-testid={testid}>
      <div className="mb-2 flex items-center gap-2">
        <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${tones[tone]}`}><Icon className="h-4 w-4" /></span>
        <h2 className="font-heading text-sm font-bold text-slate-800">{title}</h2>
        <span className="text-xs font-semibold text-slate-400">{count}</span>
      </div>
      {children}
    </section>
  );
}

function Empty({ label }) {
  return <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50/60 px-3 py-3 text-xs text-slate-400">{label}</p>;
}

function RowLink({ testid, primary, secondary, onClick, right }) {
  return (
    <button data-testid={testid} onClick={onClick} className="flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 text-left transition-shadow hover:shadow-sm">
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-slate-800">{primary}</span>
        {secondary && <span className="block truncate text-xs text-slate-400">{secondary}</span>}
      </span>
      {right}
      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
    </button>
  );
}

export default function Today() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [outcomeVisit, setOutcomeVisit] = useState(null);

  const load = () => todayApi.get().then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  const intel = data?.intelligence || {};
  const fu = intel.followups || {};

  const actFollowup = async (id, kind) => {
    try {
      if (kind === "accept") { await followupsApi.accept(id); toast.success("Follow-up scheduled"); }
      else { await followupsApi.decline(id, {}); toast.success("Dismissed"); }
      load();
    } catch (e) { toast.error("Could not update follow-up"); }
  };
  const intelCount = (intel.new_strong_prospects?.length || 0) + (intel.new_website_intent?.length || 0)
    + (intel.new_hotspots?.length || 0) + (intel.unclaimed_high_priority?.length || 0)
    + (intel.newly_assigned_to_me?.length || 0);

  const completeTask = async (id, e) => {
    e.stopPropagation();
    try { await tasksApi.complete(id); toast.success("Task completed"); load(); } catch { toast.error("Could not complete task"); }
  };

  const go = (orgId) => navigate(`/leads/${orgId}`);

  return (
    <div className="mx-auto max-w-3xl px-4 py-5 md:py-8">
      <div className="rounded-2xl border-l-4 border-l-teal-500 bg-slate-900 p-5 text-white shadow-md" data-testid="today-welcome-card">
        <p className="text-xs font-semibold uppercase tracking-widest text-teal-300">Today{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</p>
        <h1 className="mt-1 font-heading text-xl font-extrabold sm:text-2xl">Your operational day</h1>
        <p className="mt-1 text-sm text-slate-300">Deterministic, real data — overdue work, today's visits and opportunities needing action. Times use Europe/London.</p>
      </div>

      {!data ? (
        <div className="mt-6 space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl border border-slate-200 bg-white" />)}</div>
      ) : (
        <>
          <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4" data-testid="today-availability-strip">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-sm font-bold text-slate-800">Availability today</h2>
              <button data-testid="today-manage-availability" onClick={() => navigate("/availability")} className="text-xs font-semibold text-teal-700 hover:underline">Manage</button>
            </div>
            {data.availability_today?.length ? (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {data.availability_today.map((a) => (
                  <span key={a.id} data-testid={`today-avail-${a.id}`} className="rounded-full border border-teal-200 bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700">
                    {availTypeLabel[a.availability_type]}{a.full_day ? " · All day" : ` · ${a.start_time}–${a.end_time}`}
                  </span>
                ))}
              </div>
            ) : <p className="mt-1 text-xs text-slate-400">No availability set for today.</p>}
            {data.next_field_day && (
              <p className="mt-2 text-xs text-slate-500" data-testid="today-next-field-day">
                Next field day: <span className="font-semibold text-slate-700">{new Date(data.next_field_day.start_at).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" })}</span>
              </p>
            )}
          </div>

          <section className="mt-5 rounded-xl border border-teal-200 bg-white p-4" data-testid="today-sales-briefing">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-heading text-sm font-bold text-slate-800">Your next 30 minutes of sales</h2>
              <span className="text-xs text-slate-500">{data.sales_briefing?.allocated_minutes || 0} min planned</span>
            </div>
            {data.sales_briefing?.availability_required ? (
              <p className="mt-2 text-xs text-slate-500">Set an active phone or desk availability window to see suitable actions.</p>
            ) : data.sales_briefing?.actions?.length ? (
              <div className="mt-3 space-y-2">
                {data.sales_briefing.actions.map((a) => (
                  <RowLink key={a.organisation_id} testid={`today-sales-action-${a.organisation_id}`}
                    primary={`${a.organisation_name} · ${a.product_code}`}
                    secondary={`${a.channel} · ${a.estimated_minutes} min · ${(a.why || []).join("; ") || "Open opportunity"}`}
                    onClick={() => go(a.organisation_id)} />
                ))}
              </div>
            ) : (
              <p className="mt-2 text-xs text-slate-500">No suitable open opportunities in this window. Review your leads or availability.</p>
            )}
          </section>

          <TodayChatCard />

          <PlanTodayPanel />

          {/* Contextual push opt-in (native iOS only; hidden once granted/denied or snoozed) */}
          <NotificationPermissionPrompt />

          <Section title="Overdue follow-ups" icon={AlertCircle} tone="rose" count={data.overdue_followups.length} testid="today-overdue">
            {data.overdue_followups.length === 0 ? <Empty label="Nothing overdue. Nice." /> :
              <div className="space-y-2">{data.overdue_followups.map((t) => (
                <RowLink key={t.id} testid={`today-task-${t.id}`} primary={t.title} secondary={`Due ${fmtTime(t.due_at)}${t.source === "followup" ? " · Follow-up" : ""}`} onClick={() => go(t.organisation_id)}
                  right={<div className="flex items-center gap-2">
                    {t.source === "followup" && t.channel && <ChannelBadge channel={(t.channel || "wait").toUpperCase()} reasons={t.channel_reasons || []} testid={`today-fu-channel-${t.id}`} />}
                    <span onClick={(e) => completeTask(t.id, e)} data-testid={`today-complete-${t.id}`} className="rounded-md border border-emerald-200 bg-emerald-50 p-1.5 text-emerald-600 hover:bg-emerald-100"><CheckCircle2 className="h-4 w-4" /></span>
                  </div>} />
              ))}</div>}
          </Section>

          <Section title="Tasks due today" icon={CalendarClock} tone="amber" count={data.tasks_due_today.length} testid="today-due">
            {data.tasks_due_today.length === 0 ? <Empty label="No tasks due today." /> :
              <div className="space-y-2">{data.tasks_due_today.map((t) => (
                <RowLink key={t.id} testid={`today-task-${t.id}`} primary={t.title} secondary={`Due ${fmtTime(t.due_at)}${t.source === "followup" ? " · Follow-up" : ""}`} onClick={() => go(t.organisation_id)}
                  right={<div className="flex items-center gap-2">
                    {t.source === "followup" && t.channel && <ChannelBadge channel={(t.channel || "wait").toUpperCase()} reasons={t.channel_reasons || []} testid={`today-fu-channel-${t.id}`} />}
                    <span onClick={(e) => completeTask(t.id, e)} data-testid={`today-complete-${t.id}`} className="rounded-md border border-emerald-200 bg-emerald-50 p-1.5 text-emerald-600 hover:bg-emerald-100"><CheckCircle2 className="h-4 w-4" /></span>
                  </div>} />
              ))}</div>}
          </Section>

          <Section title="Today's planned visits" icon={MapPin} tone="teal" count={data.todays_visits.length} testid="today-visits">
            {data.todays_visits.length === 0 ? <Empty label="No visits planned for today." /> :
              <div className="space-y-2">{data.todays_visits.map((v) => (
                <RowLink key={v.id} testid={`today-visit-${v.id}`} primary={`Visit · ${fmtTime(v.planned_at)}`} secondary="Tap to record outcome" onClick={() => setOutcomeVisit(v)} />
              ))}</div>}
          </Section>

          <Section title="Upcoming field opportunities" icon={MapPin} tone="teal" count={(data.upcoming_field_opportunities || []).length} testid="today-field-ops">
            {(data.upcoming_field_opportunities || []).length === 0 ? <Empty label="No upcoming field visits." /> :
              <div className="space-y-2">{data.upcoming_field_opportunities.map((r) => (
                <RowLink key={r.visit_id} testid={`today-field-op-${r.visit_id}`} primary={r.message}
                  secondary={r.has_suggestion ? "Tap to review nearby prospects" : "Tap to plan a worthwhile trip"}
                  onClick={() => navigate(`/leads/${r.organisation_id}`)} />
              ))}</div>}
          </Section>

          <Section title="Opportunities requiring action" icon={Target} tone="teal" count={data.opportunities_requiring_action.length} testid="today-opps">
            {data.opportunities_requiring_action.length === 0 ? <Empty label="No opportunities need action today." /> :
              <div className="space-y-2">{data.opportunities_requiring_action.map((o) => (
                <RowLink key={o.id} testid={`today-opp-${o.id}`} primary={`${o.product_code} · ${o.stage}`} secondary={o.next_action || "Next action due"} onClick={() => go(o.organisation_id)} />
              ))}</div>}
          </Section>

          <Section title="Follow-ups" icon={CalendarClock} tone="teal" testid="today-followups"
            count={(fu.suggested?.length || 0) + (fu.scheduled?.length || 0) + (fu.overdue?.length || 0)}>
            {((fu.suggested?.length || 0) + (fu.scheduled?.length || 0) + (fu.overdue?.length || 0)) === 0 ?
              <Empty label="No follow-ups suggested." /> : (
              <div className="space-y-2" data-testid="today-followups-list">
                {(fu.overdue || []).map((t) => (
                  <RowLink key={t.id} testid={`today-fu-overdue-${t.id}`} primary={`Overdue · ${t.title}`}
                    secondary={`Due ${fmtTime(t.due_at)}`} onClick={() => go(t.organisation_id)} />
                ))}
                {(fu.scheduled || []).map((t) => (
                  <RowLink key={t.id} testid={`today-fu-scheduled-${t.id}`} primary={`Scheduled · ${t.title}`}
                    secondary={`Due ${fmtTime(t.due_at)}`} onClick={() => go(t.organisation_id)} />
                ))}
                {(fu.suggested || []).map((s) => (
                  <div key={s.id} data-testid={`today-fu-suggested-${s.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-teal-100 bg-teal-50/40 px-3 py-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-slate-700">{s.proposed_channel || "Review"} · {s.proposed_notes}</div>
                      <div className="text-[10px] text-slate-400">Due {fmtTime(s.proposed_due_at)} · {s.rule_key}</div>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <button data-testid={`today-fu-accept-${s.id}`} onClick={() => actFollowup(s.id, "accept")} className="rounded-md bg-teal-700 px-2 py-1 text-[11px] font-semibold text-white">Accept</button>
                      <button data-testid={`today-fu-decline-${s.id}`} onClick={() => actFollowup(s.id, "decline")} className="rounded-md border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-500">Decline</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title="New intelligence" icon={Sparkles} tone="teal" count={intelCount} testid="today-intelligence">
            {intelCount === 0 ? <Empty label="No new signals in the last 24 hours." /> : (
              <div className="space-y-2" data-testid="today-intel-list">
                {(data.intelligence?.new_strong_prospects || []).map((s) => (
                  <RowLink key={s.id} testid={`today-intel-prospect-${s.id}`} primary={`New prospect · ${s.payload?.business_name || "FSA venue"}`}
                    secondary="Fresh CaterCTRL prospect from FSA" onClick={() => s.payload?.organisation_id && go(s.payload.organisation_id)}
                    right={s.channel && <ChannelBadge channel={s.channel.channel} reasons={s.channel.reasons} testid={`today-channel-prospect-${s.id}`} />} />
                ))}
                {(data.intelligence?.new_website_intent || []).map((s) => (
                  <RowLink key={s.id} testid={`today-intel-intent-${s.id}`} primary={`Website intent · ${s.payload?.organisation_name || "Matched org"}`}
                    secondary={`Intent score ${s.payload?.intent_score ?? ""}`} onClick={() => s.payload?.organisation_id && go(s.payload.organisation_id)}
                    right={s.channel && <ChannelBadge channel={s.channel.channel} reasons={s.channel.reasons} testid={`today-channel-intent-${s.id}`} />} />
                ))}
                {(data.intelligence?.new_hotspots || []).map((s) => (
                  <RowLink key={s.id} testid={`today-intel-hotspot-${s.id}`} primary={`New hotspot · ${s.payload?.type || ""}`}
                    secondary={(s.payload?.evidence || [])[0] || "Cluster forming"} onClick={() => navigate("/hotspots")} />
                ))}
                {(data.intelligence?.unclaimed_high_priority || []).slice(0, 5).map((o) => (
                  <RowLink key={o.id} testid={`today-intel-unclaimed-${o.id}`} primary={`Unclaimed · ${o.legal_name}`}
                    secondary="Prospect with no owner" onClick={() => go(o.id)}
                    right={o.channel && <ChannelBadge channel={o.channel.channel} reasons={o.channel.reasons} testid={`today-channel-unclaimed-${o.id}`} />} />
                ))}
              </div>
            )}
            {data.intelligence?.failing_monitored_areas > 0 && (
              <button data-testid="today-failing-areas" onClick={() => navigate("/monitored-areas")}
                className="mt-2 flex w-full items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">
                <AlertCircle className="h-4 w-4" /> {data.intelligence.failing_monitored_areas} monitored area(s) failing to scan
              </button>
            )}
          </Section>

          <Section title="Recent activity" icon={ActivityIcon} count={data.recent_activity.length} testid="today-activity">
            {data.recent_activity.length === 0 ? <Empty label="No recent activity yet." /> :
              <div className="space-y-1.5">{data.recent_activity.slice(0, 10).map((a) => (
                <div key={a.id} className="flex items-start gap-2 rounded-lg border border-slate-100 bg-white px-3 py-2 text-xs">
                  <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-500" />
                  <span className="min-w-0 flex-1"><span className="font-medium text-slate-700">{a.summary}</span><span className="block text-slate-400">{a.activity_type.replace(/_/g, " ")} · {fmtTime(a.created_at)}</span></span>
                </div>
              ))}</div>}
          </Section>

          <div className="mt-6 grid gap-2 sm:grid-cols-2">
            <button data-testid="quick-link-add-org" onClick={() => navigate("/leads?new=1")} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-3 text-sm font-semibold text-slate-700 hover:shadow-sm"><Plus className="h-4 w-4 text-teal-600" /> Add organisation</button>
            <button data-testid="quick-link-view-leads" onClick={() => navigate("/leads")} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-3 text-sm font-semibold text-slate-700 hover:shadow-sm"><Building2 className="h-4 w-4 text-teal-600" /> View leads</button>
          </div>
        </>
      )}

      {/* Record a planned visit's outcome directly from Today (≤3 taps: row → outcome → submit) */}
      <VisitOutcomeDialog open={!!outcomeVisit} onOpenChange={(v) => !v && setOutcomeVisit(null)} visit={outcomeVisit} onSaved={load} />
    </div>
  );
}
